import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";

import Typography from "@mui/material/Typography";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import InputAdornment from "@mui/material/InputAdornment";
import { IconButton } from "@mui/material";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { doRegister } from "./authSlice";

// ─── Enterprise design tokens ────────────────────────────────────────────────
const tokens = {
  navy:    "#0A1628",
  navyMid: "#112240",
  accent:  "#2563EB",
  accentHover: "#1D4ED8",
  slate:   "#64748B",
  border:  "#E2E8F0",
  surface: "#F8FAFC",
  white:   "#FFFFFF",
  danger:  "#DC2626",
  textPrimary:   "#0F172A",
  textSecondary: "#475569",
};

// ─── Global style injection ───────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; }

    .reg-root {
      display: flex;
      min-height: 100vh;
      font-family: 'Inter', system-ui, sans-serif;
      background: ${tokens.surface};
    }

   /* ── Left panel (identical to RegisterPage) ── */
    .login-panel {
      width: 380px;
      flex-shrink: 0;
      background: ${tokens.navy};
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 48px 40px;
      position: relative;
      overflow: hidden;
    }
    .login-panel::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        radial-gradient(ellipse 600px 500px at -100px 120%, #2563EB22 0%, transparent 70%),
        radial-gradient(ellipse 400px 400px at 420px 500px, #2563EB18 0%, transparent 70%);
      pointer-events: none;
    }
    .login-panel-logo img {
      width:170px;
    }
    .login-panel-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: rgba(37,99,235,0.18);
      border: 1px solid rgba(37,99,235,0.35);
      border-radius: 20px;
      padding: 4px 12px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .08em;
      text-transform: uppercase;
      color: #93C5FD;
      margin-bottom: 20px;
    }
    .login-panel-headline {
      font-size: 28px;
      font-weight: 700;
      line-height: 1.3;
      color: ${tokens.white};
      margin: 0 0 16px;
    }
    .login-panel-quote {
      font-size: 14px;
      line-height: 1.7;
      color: rgba(255,255,255,0.65);
      border-left: 2px solid ${tokens.accent};
      padding-left: 16px;
      margin: 0 0 40px;
    }
    .login-panel-stats {
      display: flex;
      gap: 32px;
    }
    .login-stat-num {
      font-size: 24px;
      font-weight: 700;
      color: ${tokens.white};
    }
    .login-stat-label {
      font-size: 11px;
      color: rgba(255,255,255,0.5);
      margin-top: 2px;
      letter-spacing: .04em;
    }
    .login-panel-img {
      width: 100%;
      border-radius: 12px;
      overflow: hidden;
      margin: 32px 0;
    }
    .login-panel-img img {
      width: 100%;
      display: block;
      object-fit: cover;
      border-radius: 12px;
      opacity: 0.85;
    }
    .login-panel-brands {
      border-top: 1px solid rgba(255,255,255,0.08);
      padding-top: 24px;
    }
    .login-brands-label {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .08em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.35);
      margin-bottom: 16px;
    }
    .login-brands-grid {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      align-items: center;
    }
    .login-brands-grid img {
      height: 20px;
      filter: brightness(0) invert(1);
      opacity: 0.4;
    }


    /* ── Right / form side ── */
    .reg-form-side {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 48px 32px;
      overflow-y: auto;
    }
    .reg-form-card {
      width: 100%;
      max-width: 480px;
    }
    .reg-form-header {
      margin-bottom: 32px;
    }
    .reg-form-eyebrow {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .1em;
      text-transform: uppercase;
      color: ${tokens.accent};
      margin-bottom: 8px;
    }
    .reg-form-title {
      font-size: 26px;
      font-weight: 700;
      color: ${tokens.textPrimary};
      margin: 0 0 6px;
    }
    .reg-form-sub {
      font-size: 14px;
      color: ${tokens.textSecondary};
    }

    /* ── Field rows ── */
    .reg-row {
      display: flex;
      gap: 16px;
    }
    .reg-field {
      flex: 1;
      margin-bottom: 18px;
    }
    .reg-label {
      display: block;
      font-size: 13px;
      font-weight: 500;
      color: ${tokens.textPrimary};
      margin-bottom: 6px;
    }
    .reg-label span { color: ${tokens.danger}; margin-left: 2px; }

    /* MUI TextField enterprise overrides */
    .reg-field .MuiOutlinedInput-root {
      border-radius: 8px !important;
      font-size: 14px !important;
      background: ${tokens.white} !important;
    }
    .reg-field .MuiOutlinedInput-notchedOutline {
      border-color: ${tokens.border} !important;
    }
    .reg-field .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: #94A3B8 !important;
    }
    .reg-field .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${tokens.accent} !important;
      border-width: 1.5px !important;
    }
    .reg-field .MuiInputBase-input::placeholder { color: #94A3B8; opacity: 1; }

    /* Phone input override */
    .reg-phone-wrap .react-tel-input .form-control {
      width: 100% !important;
      height: 40px !important;
      border-radius: 8px !important;
      border: 1px solid ${tokens.border} !important;
      font-size: 14px !important;
      font-family: 'Inter', system-ui, sans-serif !important;
      color: ${tokens.textPrimary} !important;
      padding-left: 52px !important;
      transition: border-color .15s;
    }
    .reg-phone-wrap .react-tel-input .form-control:focus {
      border-color: ${tokens.accent} !important;
      box-shadow: 0 0 0 2px rgba(37,99,235,0.12) !important;
      outline: none !important;
    }
    .reg-phone-wrap .react-tel-input .flag-dropdown {
      border-radius: 8px 0 0 8px !important;
      border: 1px solid ${tokens.border} !important;
      border-right: none !important;
      background: ${tokens.surface} !important;
    }
    .reg-phone-error {
      font-size: 12px;
      color: ${tokens.danger};
      margin-top: 5px;
    }

    /* Checkbox */
    .reg-checkbox-label .MuiFormControlLabel-label {
      font-size: 13px !important;
      color: ${tokens.textSecondary} !important;
    }
    .reg-checkbox-label .MuiCheckbox-root { color: ${tokens.border} !important; }
    .reg-checkbox-label .Mui-checked { color: ${tokens.accent} !important; }

    /* Submit button */
    .reg-submit-btn {
      width: 100% !important;
      height: 44px !important;
      background: ${tokens.accent} !important;
      color: #fff !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      letter-spacing: .02em !important;
      border-radius: 8px !important;
      text-transform: none !important;
      box-shadow: 0 1px 3px rgba(37,99,235,0.3) !important;
      transition: background .15s !important;
    }
    .reg-submit-btn:hover {
      background: ${tokens.accentHover} !important;
    }

    /* Divider row */
    .reg-divider {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 8px 0;
      color: ${tokens.border};
      font-size: 12px;
      color: ${tokens.slate};
    }
    .reg-divider::before, .reg-divider::after {
      content: '';
      flex: 1;
      height: 1px;
      background: ${tokens.border};
    }

    /* Footer link */
    .reg-footer-link {
      text-align: center;
      font-size: 13px;
      color: ${tokens.textSecondary};
      margin-top: 20px;
    }
    .reg-footer-link a {
      color: ${tokens.accent};
      font-weight: 500;
      text-decoration: none;
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      .reg-panel { display: none; }
      .reg-form-side { padding: 32px 16px; }
    }
  `}</style>
);

// ─── Reusable labelled field wrapper ─────────────────────────────────────────
const Field = ({ label, required, children, error }) => (
  <div className="reg-field">
    <label className="reg-label">
      {label}{required && <span>*</span>}
    </label>
    {children}
    {error && <p style={{ fontSize: 12, color: tokens.danger, margin: "4px 0 0" }}>{error}</p>}
  </div>
);

// ─── Component ───────────────────────────────────────────────────────────────
const RegisterPage = ({}) => {
  const auth = useSelector((state) => state.auth);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [alert, setAlert] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () => setConfirmPassword(!showConfirmPassword);
  const handleMouseDownConfirmPassword = () => setConfirmPassword(!showConfirmPassword);

  const [newphone, setNewPhone] = useState("");
  const [phoneError, setPhoneError] = useState(null);
  const [phonevisible, SetPhoneVisible] = useState(false);

  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
  }));

  const initialValues = {
    plan: "ACTIVE",
    source: "LABELGEN",
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    coupon: "",
    referal_id: "",
    ref_id: ""
  };

  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let params = new URLSearchParams(window.location.search);
    const trackingRefId = params.get("ref_id") || "";
    const trackingReferalId = params.get("referal_id") || "";

    if (trackingRefId || trackingReferalId) {
      setValues((prevValues) => ({
        ...prevValues,
        ref_id: trackingRefId,
        referal_id: trackingReferalId
      }));
      console.log("Captured Referral tracking payload structural metrics:", { ref_id: trackingRefId, referal_id: trackingReferalId });
    }
  }, []);

  const validator = (fieldValues) => {
    let temp = { ...errors };
    if ("name" in fieldValues)
      temp.name = fieldValues.name ? "" : "This field is required.";
    if ("email" in fieldValues)
      temp.email = fieldValues.email ? "" : "This field is required.";
    if (
      "email" in fieldValues &&
      !/^([\+\w.-]+)@(\[(\d{1,3}\.){3}|(?!hotmail|gmail|yahoo|proton|outlook|dofibra|qq)(([a-zA-Z\d-]+\.)+))([a-zA-Z]{2,4}|\d{1,3})(\]?)$/.test(
        fieldValues && fieldValues.email
      )
    ) {
      temp.email = "Enter valid business email";
    }
    if ("password" in fieldValues)
      temp.password = fieldValues.password ? "" : "This field is required.";
    if (
      (fieldValues && fieldValues.password && fieldValues.password.length < 8) ||
      fieldValues.password === null
    )
      temp.password = "Password required minimum 8 characters";
    if ("password_confirmation" in fieldValues)
      temp.password_confirmation = fieldValues.password_confirmation
        ? ""
        : "This field is required.";

    if (fieldValues && fieldValues.coupon && fieldValues.coupon.length < 5)
      temp.coupon = "coupon code required minimum 5 characters";
    else {
      temp.coupon = "";
    }
    setErrors({ ...temp });

    if (fieldValues == values) return Object.values(temp).every((x) => x == "");
  };

  const onInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
    validator({ [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const isValid = validator(values);
    console.log(newphone.length);

    if (isValid && newphone !== "" && newphone.length >= 10) {
      const finalPayload = { ...values, phone: newphone };
      setPhoneError(null);
      dispatch(doRegister(finalPayload));
      setOpen(true);
    } else {
      if (newphone == "" && newphone.length == 0) {
        setPhoneError("This field is required.");
        SetPhoneVisible(false);
      } else if (newphone.length < 11) {
        setPhoneError("Phone number must be at least 10 numbers");
        SetPhoneVisible(false);
      }
      setOpen(true);
    }
  };

  const snackhandleClose = () => {
    setAlert(false);
    setSnackOpen(false);
  };

  const handleOnChange = (value) => {
    setNewPhone(value);
    setPhoneError(null);
  };

  const handleOnChangephone = (e) => {
    setNewPhone(e.target.value);
  };

  useEffect(() => {
    if (auth && auth.notification && auth.notification.messageContent) {
      setAlert(true);
      setSnackOpen(true);
    }
  }, [auth]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyles />

      {alert && (
        <Snackbar
          open={snackOpen}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={6000}
          onClose={snackhandleClose}
        >
          <Alert
            severity={
              auth?.notification?.messageType === "error" ? "error" : "success"
            }
            sx={{ justifyContent: "center" }}
          >
            <span>{auth?.notification?.messageContent}</span>
          </Alert>
        </Snackbar>
      )}

      <div className="reg-root">
        {/* ── Left panel ── */}
        <aside className="login-panel">
          <div className="login-panel-logo">
            <a href="/">
              <img src="/assets/images/shippingchimp-logo-white.png" alt="ShippingChimp" />
            </a>
          </div>

          <div>
            <div className="login-panel-badge">
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                <circle cx="4" cy="4" r="4" fill="#60A5FA" />
              </svg>
              Trusted by 500+ brands
            </div>
            <h2 className="login-panel-headline">
              Smarter shipping,<br />faster delivery.
            </h2>
            <p className="login-panel-quote">
              ShippingChimp beats all the major larger shipping services out there. Their app works great with our Shopify store and everything integrates easily.
            </p>

            {/* Register image reused */}
            <div className="login-panel-img">
              <img src="/assets/images/register-img.png" alt="ShippingChimp platform" />
            </div>

            <div className="login-panel-stats">
              <div>
                <div className="login-stat-num">99.4%</div>
                <div className="login-stat-label">On-time delivery</div>
              </div>
              <div>
                <div className="login-stat-num">2-min</div>
                <div className="login-stat-label">Setup time</div>
              </div>
              <div>
                <div className="login-stat-num">24/7</div>
                <div className="login-stat-label">Support</div>
              </div>
            </div>
          </div>

          <div className="login-panel-brands">
            <p className="login-brands-label">Trusted by Canadian brands</p>
            <div className="login-brands-grid">
              <img src="/assets/images/tawse.png" alt="Tawse" />
              <img src="/assets/images/sleepcountry.png" alt="Sleep Country" />
              <img src="/assets/images/hatley.png" alt="Hatley" />
              <img src="/assets/images/homedics.png" alt="HoMedics" />
            </div>
          </div>
        </aside>


        {/* ── Form side ── */}
        <main className="reg-form-side">
          <div className="reg-form-card">
            {/* Mobile logo */}
            <div style={{ display: "none", marginBottom: 24 }} className="reg-mobile-logo">
              <a href="/login">
                <img src="/assets/images/shippingchimp_logo.png" alt="ShippingChimp" style={{ height: 28 }} />
              </a>
            </div>

            <div className="reg-form-header">
              <p className="reg-form-eyebrow">Get started — it's free</p>
              <h1 className="reg-form-title">Create your account</h1>
              <p className="reg-form-sub">Set up in under 2 minutes. No credit card required.</p>
            </div>

            {/* Name + Email row */}
            <div className="reg-row">
              <Field label="Full name" required error={errors.name}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="name"
                  placeholder="John Doe"
                  value={values.name}
                  onChange={onInputChange}
                  error={!!errors.name}
                />
              </Field>
              <Field label="Work email" required error={errors.email}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  variant="outlined"
                  size="small"
                  name="email"
                  placeholder="name@company.com"
                  value={values.email}
                  onChange={onInputChange}
                  error={!!errors.email}
                />
              </Field>
            </div>

            {/* Phone */}
            <Field label="Phone number" required>
              <div className="reg-phone-wrap">
                <PhoneInput
                  name="phone"
                  autoCorrect="off"
                  placeholder="Enter a valid phone number"
                  country={"ca"}
                  onlyCountries={["ca", "us", "gb", "in"]}
                  value={newphone}
                  onChange={handleOnChange}
                />
              </div>
              <input
                style={{ display: "none" }}
                placeholder="Enter a Valid Phone Number"
                autoCorrect="off"
                id="multipleErrorInput4"
                name="phone"
                value={newphone}
                onChange={handleOnChangephone}
              />
              {!phonevisible && phoneError && (
                <p className="reg-phone-error">{phoneError}</p>
              )}
            </Field>

            {/* Password row */}
            <div className="reg-row">
              <Field label="Password" required error={errors.password}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="password"
                  value={values.password}
                  placeholder="Min. 8 characters"
                  required
                  type={showPassword ? "text" : "password"}
                  onChange={onInputChange}
                  error={!!errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Field>
              <Field label="Confirm password" required error={errors.password_confirmation}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="password_confirmation"
                  placeholder="Re-enter password"
                  value={values.password_confirmation}
                  required
                  type={showConfirmPassword ? "text" : "password"}
                  onChange={onInputChange}
                  error={!!errors.password_confirmation}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="toggle password visibility"
                          onClick={handleClickShowConfirmPassword}
                          onMouseDown={handleMouseDownConfirmPassword}
                        >
                          {showConfirmPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Field>
            </div>

            {/* Coupon */}
            <Field label="Coupon code" error={errors.coupon}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                name="coupon"
                placeholder="Enter coupon code (optional)"
                value={values.coupon}
                onChange={onInputChange}
                error={!!errors.coupon}
              />
            </Field>

            {/* Checkbox */}
            <div style={{ marginBottom: 20 }}>
              <FormGroup>
                <FormControlLabel
                  className="reg-checkbox-label"
                  control={<Checkbox size="small" />}
                  label="Keep me updated on product news and offers"
                />
              </FormGroup>
            </div>

            {/* Submit */}
            <Button
              variant="contained"
              className="reg-submit-btn"
              onClick={handleSubmit}
            >
              Complete signup →
            </Button>

            {/* Footer */}
            <p className="reg-footer-link">
              Already have an account? <a href="/login">Sign in</a>
            </p>
          </div>
        </main>
      </div>
    </>
  );
};

export default RegisterPage;